import React, { useState, useContext, useEffect } from "react";
import { Container, Row, Col } from "reactstrap";
import { useLocation, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import axios from "axios";
import useLocalStorage from "react-use-localstorage";
import queryString from "query-string";

import UserContext from "../../contexts/user";
import EventContext from "../../contexts/event";

import SidebarMain from "../../components/sidebar-main";
import EditProfileModal from "../../components/edit-profile-modal";
import LocaleSwitcher from "../../components/locale-switcher";
import { PreLoader } from "../../components/preloader";
import { Presentation, ScheduleEndpointResponseObject } from "../../types/presentation";
import Timeline from "./timeline";
import PresentationCards from "./presentationCards";
import Filter from "./filter";
import Rooms from "./rooms";
import { calculateTimelineHeight } from './utils/zoomHelpers';
import { LeftArrow, RightArrow } from "./ChangerButton";

const Synopsis: React.FC<{
    locale: string;
    setLocale: (arg0: string) => void;
}> = ({ locale, setLocale }) => {
    const intl = useIntl();
    const { event_slug } = useParams<{ event_slug: string }>();
    const location = useLocation();
    const [isIframe] = useState(queryString.parse(location.search).iframe);

    const userContext = useContext(UserContext);
    const eventContext = useContext(EventContext);
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [eventPassword] = useLocalStorage(event_slug || "");
    const [filter, setFilter] = useState({
        speakers: [],
        rooms: [],
        associations: [],
        dates: [],
        activeQuizzesOnly: false,
    });
    const [zoomLevel] = useState([1, 25, 50, 75, 100]);
    const [zoomIndex, setZoomIndex] = useState(2);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('all');

    // Responsive navigation state
    const [currentDayIndex, setCurrentDayIndex] = useState(0);
    const [currentRoomIndices, setCurrentRoomIndices] = useState<{ [day: string]: number }>({});
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
    const [scheduledMap, setScheduledMap] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const handleResize = () => {
            try {
                setIsMobileOrTablet(window.innerWidth < 1024);
            } catch (error) {
                console.error('Error handling window resize:', error);
            }
        };
        try {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        } catch (error) {
            console.error('Error setting up resize listener:', error);
        }
    }, []);

    const dynamicTimelineHeight = calculateTimelineHeight(zoomLevel, zoomIndex);

    // Timeline configuration
    const TIMELINE_CONFIG = {
        containerHeight: 1080,
        startHour: 1,
        endHour: 24
    };

    // Initial event data query
    const {
        data: eventData,
        isLoading: eventLoading,
        error: eventError,
        refetch: refetchEventData
    } = useQuery(
        "event",
        async () => {
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/public/schedule/${event_slug}`;
            const response = await axios.get<ScheduleEndpointResponseObject>(url, {
                params: { password: eventPassword },
            });
            return response.data;
        },
        {
            enabled: !!event_slug,
            onSuccess: (resp) => {
                if (resp?.data?.event) {
                    userContext.setCurrentEvent(resp.data.event);
                }
            },
            retry: 2,
            retryDelay: 1000,
        }
    );

    // Filtered presentations query
    const {
        data: filteredData,
        isLoading: filterLoading,
        error: filterError,
        refetch: refetchFiltered
    } = useQuery(
        ["filtered-presentations", event_slug, search, filterType, userContext.user?.speaker_id],
        async () => {

            // Don't fetch filtered data if we don't have basic event data or if no filters applied
            if (!eventData?.data?.event?._id || (!search && filterType === 'all')) {
                return null;
            }
            const userId = userContext.user?.id
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/presentation/filter`;
            const params: any = {
                eventId: eventData.data.event._id,
                password: eventPassword || event_slug,
                userId: userId,
                slug: event_slug,
            };

            if (search) {
                params.search = search;
            }

            if (filterType && filterType !== 'all') {
                params.type = filterType;
                if (filterType === 'my-presentations' && userContext.user?.speaker_id) {
                    params.userId = userContext.user.speaker_id;
                }
            }

            const response = await axios.get(url, { params });
            return response.data;
        },
        {
            enabled: !!eventData?.data?.event?._id && (!!search || filterType !== 'all'),
            retry: 2,
            retryDelay: 1000,
        }
    );

    // Determine which presentations to show
    const currentPresentations = React.useMemo(() => {
        try {
            // If we have filtered data, use it        
            if (filteredData?.data) {
                return Array.isArray(filteredData.data) ? filteredData.data : filteredData.data.presentations || [];
            }
            // Otherwise use the original event presentations
            return eventData?.data?.presentations || [];
        } catch (error) {
            console.error('Error processing current presentations:', error);
            return [];
        }
    }, [filteredData, eventData]);

    const updatePresentation = (presentation: any) => {
        try {
            if (presentation && !filteredData) {
                refetchEventData();
            } else {
                refetchFiltered();
            }
        } catch (error) {
            console.error('Error updating presentation:', error);
        }
    };

    const groupPresentationsByDayAndRoom = (presentations: any[]) => {
        try {
            const dayMap: { [day: string]: { [roomId: string]: any[] } } = {};
            presentations.forEach(p => {
                const day = `Day ${p.day}`;
                const roomId = p.room_id || "unassigned";
                if (!dayMap[day]) dayMap[day] = {};
                if (!dayMap[day][roomId]) dayMap[day][roomId] = [];
                dayMap[day][roomId].push(p);
            });
            return dayMap;
        } catch (error) {
            console.error('Error grouping presentations by day and room:', error);
            return {};
        }
    };

    // Helper function to generate dashed lines for each hour
    const generateDashedLines = (containerHeight: number) => {
        try {
            const timeSlots = Array.from(
                { length: TIMELINE_CONFIG.endHour - TIMELINE_CONFIG.startHour + 1 },
                (_, i) => i
            );
            const hourHeight = containerHeight / timeSlots.length;
            const timelineOffset = 20;

            return timeSlots.map((_, index) => (
                <div
                    key={`dashed-line-${index}`}
                    className="tw-absolute tw-left-0 tw-right-0 tw-border tw-border-dashed tw-pointer-events-none"
                    style={{
                        borderColor: '#bbbdc0',
                        top: `${(index * hourHeight) + timelineOffset + 15}px`,
                        zIndex: 1,
                        borderTopWidth: '0px',
                    }}
                />
            ));
        } catch (error) {
            console.error('Error generating dashed lines:', error);
            return [];
        }
    };

    // Loading state
    if (eventLoading) return <PreLoader />;

    // Error state
    if (eventError || !eventData) {
        console.error("Error fetching event data:", eventError);
        return <div>{intl.formatMessage({ id: 'error.retry.message' })}</div>;
    }

    // Filter error (non-blocking)
    if (filterError) {
        console.error("Error fetching filtered data:", filterError);
    }

    const { data: mainEventData } = eventData;

    if (!mainEventData) {
        return <div>{intl.formatMessage({ id: 'error.retry.message' })}</div>;
    }

    const presentationsByDayAndRoom = groupPresentationsByDayAndRoom(currentPresentations);
    const sortedDays = Object.entries(presentationsByDayAndRoom).sort(([dayA], [dayB]) => {
        const dayNumA = parseInt(dayA.split(" ")[1]);
        const dayNumB = parseInt(dayB.split(" ")[1]);
        return dayNumA - dayNumB;
    });

    // Responsive: Only show one day/room at a time on mobile/tablet
    const visibleDay = isMobileOrTablet ? sortedDays[currentDayIndex] : null;
    const visibleRooms = visibleDay ? Object.entries(visibleDay[1]) : [];
    const currentRoomIndex = visibleDay ? (currentRoomIndices[visibleDay[0]] || 0) : 0;
    const visibleRoom = isMobileOrTablet && visibleRooms.length > 0 ? visibleRooms[currentRoomIndex] : null;

    // Arrow handlers
    const handleDayLeft = () => {
        try {
            setCurrentDayIndex((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error navigating to previous day:', error);
        }
    };
    const handleDayRight = () => {
        try {
            setCurrentDayIndex((prev) => Math.min(sortedDays.length - 1, prev + 1));
        } catch (error) {
            console.error('Error navigating to next day:', error);
        }
    };
    const handleRoomLeft = () => {
        try {
            if (!visibleDay) return;
            setCurrentRoomIndices((prev) => ({
                ...prev,
                [visibleDay[0]]: Math.max(0, (prev[visibleDay[0]] || 0) - 1)
            }));
        } catch (error) {
            console.error('Error navigating to previous room:', error);
        }
    };
    const handleRoomRight = () => {
        try {
            if (!visibleDay) return;
            setCurrentRoomIndices((prev) => ({
                ...prev,
                [visibleDay[0]]: Math.min(visibleRooms.length - 1, (prev[visibleDay[0]] || 0) + 1)
            }));
        } catch (error) {
            console.error('Error navigating to next room:', error);
        }
    };

    return (
        <div className={`grid-contianer synopsis-page ${isIframe ? "iframe-mode-container" : ""}`}>
            <SidebarMain
                event={userContext.currentEvent}
                aggregates={mainEventData.aggregates}
                filter={filter}
                setFilter={setFilter}
                openProfileModal={openProfileModal}
                setOpenProfileModal={setOpenProfileModal}
                setSchedule="synopsis"
            />

            {/* Loading overlay for filtering */}
            {filterLoading && (
                <div className="tw-inset-0 tw-bg-black tw-bg-opacity-25 tw-flex tw-items-center tw-justify-center"
                    style={{
                        position: 'fixed',
                        zIndex: 9999
                    }}>
                    <PreLoader />
                </div>
            )}

            {/* Main content area */}
            <div className="tw-overflow-x-auto tw-relative synopsis-scroll">
                {sortedDays.length === 0 ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
                        <p className="tw-text-gray-500 tw-text-lg">
                            {search || filterType !== 'all'
                                ? intl.formatMessage({ id: 'no.results.found', defaultMessage: 'No presentations found matching your criteria.' })
                                : intl.formatMessage({ id: 'no.presentations', defaultMessage: 'No presentations available.' })
                            }
                        </p>
                    </div>
                ) : (
                    isMobileOrTablet ? (
                        visibleDay && (
                            <div key={visibleDay[0]} className="tw-mb-8">
                                {/* Fixed header with day navigation */}
                                <div className="tw-w-full" style={{ position: "sticky", top: 0, zIndex: 50 }}>
                                    <div className="tw-h-28 tw-w-screen tw-flex tw-items-end lg:tw-ml-3" style={{ marginTop: "3.7rem", borderBottom: "1px solid #A5A8AF", background: "#d7d8d9" }}>
                                        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-w-full">
                                            <div className="tw-w-full lg:tw-w-auto tw-flex tw-flex-col lg:tw-items-start tw-items-center tw-text-sm tw-font-semibold tw-bg-customGray tw-text-white lg:tw-text-customColor tw-p-3 lg:tw-ml-2 lg:tw-bg-transparent">
                                                <div className="tw-w-full tw-relative tw-flex tw-flex-col tw-items-center lg:tw-static lg:tw-flex-row lg:tw-items-center">
                                                    <span className="tw-absolute tw-left-0 tw-top-3 -tw-translate-y-1/2 lg:tw-hidden" onClick={handleDayLeft}>
                                                        <LeftArrow color="#FFFFFF" />
                                                    </span>
                                                    <div className="tw-w-full tw-text-center lg:tw-text-left">
                                                        <span>
                                                            {intl.formatMessage({ id: 'day', defaultMessage: 'Day' })} {visibleDay[0].split(" ")[1]}
                                                        </span>
                                                        <br />
                                                        <span className="tw-whitespace-nowrap">
                                                            {visibleRooms.length > 0 && visibleRooms[0][1].length > 0 && (
                                                                new Date(visibleRooms[0][1][0].date).toLocaleDateString(
                                                                    locale === "fr" ? "fr-FR" : "en-US",
                                                                    { weekday: "long", month: "short", day: "numeric", year: "numeric" }
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                    <span className="tw-absolute tw-right-0 tw-top-3 -tw-translate-y-1/2 lg:tw-hidden" onClick={handleDayRight}>
                                                        <RightArrow color="#FFFFFF" />
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Room navigation for mobile/tablet */}
                                            <Rooms
                                                presentation={currentPresentations}
                                                day={visibleDay[0]}
                                                onRoomLeft={handleRoomLeft}
                                                onRoomRight={handleRoomRight}
                                                currentRoomIndex={currentRoomIndex}
                                                isMobileOrTablet={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Timeline and presentation content for selected room */}
                                <div className="tw-flex tw-flex-row tw-relative">
                                    {isMobileOrTablet ? (
                                        <Timeline
                                            containerHeight={TIMELINE_CONFIG.containerHeight}
                                            startHour={TIMELINE_CONFIG.startHour}
                                            endHour={TIMELINE_CONFIG.endHour}
                                            zoomLevel={zoomLevel}
                                            zoomIndex={zoomIndex}
                                            isMobileOrTablet={isMobileOrTablet}
                                            presentationDate={visibleRoom && visibleRoom[1] && visibleRoom[1][0] ? visibleRoom[1][0].date : ''}
                                            presentationDay={visibleRoom && visibleRoom[1] && visibleRoom[1][0] ? visibleRoom[1][0].day : 1}
                                        />
                                    ) : (
                                        <Timeline
                                            containerHeight={TIMELINE_CONFIG.containerHeight}
                                            startHour={TIMELINE_CONFIG.startHour}
                                            endHour={TIMELINE_CONFIG.endHour}
                                            zoomLevel={zoomLevel}
                                            zoomIndex={zoomIndex}
                                            isMobileOrTablet={isMobileOrTablet}
                                        />
                                    )}
                                    <div className="tw-h-screen tw-relative">
                                        <div className="tw-flex tw-h-full lg:tw-ml-4">
                                            {visibleRoom && (
                                                <div
                                                    key={visibleRoom[0]}
                                                    className={`tw-relative tw-w-full tw-mx-2 lg:tw-p-4 lg:tw-w-screen`}
                                                    style={{
                                                        height: `${dynamicTimelineHeight + 35}px`,
                                                    }}
                                                >
                                                    <div
                                                        className={`tw-relative tw-w-full tw-h-full tw-bg-transparent ${currentRoomIndex % 2 === 1 ? 'lg:tw-bg-[#d7d8d9]' : 'lg:tw-bg-[#4B4F5A1A]'}`}
                                                    >
                                                        <PresentationCards
                                                            presentation={visibleRoom[1]}
                                                            updatePresentation={updatePresentation}
                                                            day={visibleDay[0]}
                                                            rooms={visibleDay[1]}
                                                            containerHeight={dynamicTimelineHeight}
                                                            startHour={TIMELINE_CONFIG.startHour}
                                                            endHour={TIMELINE_CONFIG.endHour}
                                                            event={userContext.currentEvent}
                                                            zoomLevel={zoomLevel}
                                                            zoomIndex={zoomIndex}
                                                            isMobileOrTablet={isMobileOrTablet}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    ) : (
                        sortedDays.map(([day, rooms]) => (
                            <div key={day} className="tw-mb-8">
                                {/* Fixed header */}
                                <div className="tw-w-full"
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 50
                                    }}>
                                    <div
                                        className="tw-h-28 tw-w-screen tw-flex tw-items-end lg:tw-ml-3"
                                        style={{
                                            marginTop: "3.7rem",
                                            borderBottom: "1px solid #A5A8AF",
                                            background: "#d7d8d9",
                                        }}
                                    >
                                        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-w-full">
                                            <div
                                                className="tw-w-full lg:tw-w-auto tw-flex tw-flex-col lg:tw-items-start tw-items-center tw-text-sm tw-font-semibold tw-bg-customGray tw-text-white lg:tw-text-customColor tw-p-3 lg:tw-ml-2 lg:tw-bg-transparent"
                                            >
                                                <div className="tw-w-full tw-relative tw-flex tw-flex-col tw-items-center lg:tw-static lg:tw-flex-row lg:tw-items-center">
                                                    <span className="tw-absolute tw-left-0 tw-top-3 -tw-translate-y-1/2 lg:tw-hidden">
                                                        <LeftArrow color="#FFFFFF" />
                                                    </span>

                                                    <div className="tw-w-full tw-text-center lg:tw-text-left">
                                                        <span>
                                                            {intl.formatMessage({ id: 'day', defaultMessage: 'Day' })} {day.split(" ")[1]}
                                                        </span>
                                                        <br />
                                                        <span className="tw-whitespace-nowrap">
                                                            {Object.keys(rooms).length > 0 && rooms[Object.keys(rooms)[0]].length > 0 && (
                                                                new Date(rooms[Object.keys(rooms)[0]][0].date).toLocaleDateString(
                                                                    locale === "fr" ? "fr-FR" : "en-US",
                                                                    {
                                                                        weekday: "long",
                                                                        month: "short",
                                                                        day: "numeric",
                                                                        year: "numeric",
                                                                    }
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                    <span className="tw-absolute tw-right-0 tw-top-3 -tw-translate-y-1/2 lg:tw-hidden">
                                                        <RightArrow color="#FFFFFF" />
                                                    </span>
                                                </div>
                                            </div>

                                            <Rooms presentation={currentPresentations} day={day} />
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline and presentation content */}
                                <div className="tw-flex tw-flex-row tw-relative ">
                                    {isMobileOrTablet ? (
                                        <Timeline
                                            containerHeight={TIMELINE_CONFIG.containerHeight}
                                            startHour={TIMELINE_CONFIG.startHour}
                                            endHour={TIMELINE_CONFIG.endHour}
                                            zoomLevel={zoomLevel}
                                            zoomIndex={zoomIndex}
                                            isMobileOrTablet={isMobileOrTablet}
                                            presentationDate={rooms && Object.values(rooms)[0] && Object.values(rooms)[0][0] ? Object.values(rooms)[0][0].date : ''}
                                            presentationDay={rooms && Object.values(rooms)[0] && Object.values(rooms)[0][0] ? Object.values(rooms)[0][0].day : 1}
                                        />
                                    ) : (
                                        <Timeline
                                            containerHeight={TIMELINE_CONFIG.containerHeight}
                                            startHour={TIMELINE_CONFIG.startHour}
                                            endHour={TIMELINE_CONFIG.endHour}
                                            zoomLevel={zoomLevel}
                                            zoomIndex={zoomIndex}
                                            isMobileOrTablet={isMobileOrTablet}
                                        />
                                    )}

                                    {/* Dashed lines background */}
                                    <div className="tw-absolute tw-inset-0 tw-pointer-events-none tw-w-screen tw-left-10" style={{ zIndex: 1 }}>
                                        {generateDashedLines(dynamicTimelineHeight)}
                                    </div>

                                    {/* Room columns */}
                                    <div className="tw-h-screen tw-relative">
                                        <div className="tw-flex tw-h-full lg:tw-ml-4">
                                            {Object.entries(rooms).map(([roomId, presentations], index) => (
                                                <div
                                                    key={roomId}
                                                    className="tw-p-4 tw-relative lg:tw-w-96"
                                                    style={{
                                                        backgroundColor: index % 2 === 1 ? "#d7d8d9" : "#4B4F5A1A",
                                                        // width: "24rem",
                                                        height: `${dynamicTimelineHeight + 35}px`,
                                                    }}
                                                >
                                                    <div className="tw-relative tw-w-full tw-h-full">
                                                        <PresentationCards
                                                            presentation={presentations}
                                                            updatePresentation={updatePresentation}
                                                            day={day}
                                                            rooms={rooms}
                                                            containerHeight={dynamicTimelineHeight}
                                                            startHour={TIMELINE_CONFIG.startHour}
                                                            endHour={TIMELINE_CONFIG.endHour}
                                                            event={userContext.currentEvent}
                                                            zoomLevel={zoomLevel}
                                                            zoomIndex={zoomIndex}
                                                            isMobileOrTablet={isMobileOrTablet}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )
                )}

                <div className="tw-w-screen">
                    <Filter
                        zoomLevel={zoomLevel}
                        zoomIndex={zoomIndex}
                        setZoomIndex={setZoomIndex}
                        search={search}
                        setSearch={setSearch}
                        filterType={filterType}
                        setFilterType={setFilterType}
                    />
                </div>
            </div>

            <EditProfileModal
                openProfileModal={openProfileModal}
                setOpenProfileModal={() => setOpenProfileModal(!openProfileModal)}
            />
        </div>
    );
};

export default Synopsis;